import {
  formatPayoutDate,
  formatPdfMoney,
  formatShiftDay,
  formatShiftTime,
} from "./payout_list_format";
import {
  normalizeIncludeXeroHours,
  PAYOUT_HOLIDAY_HOURS_MULTIPLIER,
} from "./payout_list_calculation";
import calibriRegularUrl from "../../../asset/font/Calibri-Regular.ttf";
import calibriBoldUrl from "../../../asset/font/Calibri-Bold.ttf";
import calibriItalicUrl from "../../../asset/font/Calibri-Italic.ttf";
import calibriBoldItalicUrl from "../../../asset/font/Calibri-BoldItalic.ttf";

const CALIBRI_FONTS = [
  { url: calibriRegularUrl, fileName: "Calibri-Regular.ttf", style: "normal" },
  { url: calibriBoldUrl, fileName: "Calibri-Bold.ttf", style: "bold" },
  { url: calibriItalicUrl, fileName: "Calibri-Italic.ttf", style: "italic" },
  {
    url: calibriBoldItalicUrl,
    fileName: "Calibri-BoldItalic.ttf",
    style: "bolditalic",
  },
];

let calibriFontData = null;
let calibriFontPromise = null;

const arrayBufferToBase64 = (buffer) => {
  let binary = "";
  const bytes = new Uint8Array(buffer);
  const chunkSize = 0x8000;

  for (let index = 0; index < bytes.length; index += chunkSize) {
    const chunk = bytes.subarray(index, index + chunkSize);
    binary += String.fromCharCode(...chunk);
  }

  return btoa(binary);
};

export const loadCalibriFont = async (doc) => {
  if (!calibriFontPromise) {
    calibriFontPromise = Promise.all(
      CALIBRI_FONTS.map(({ url, fileName, style }) =>
        fetch(url)
          .then((response) => response.arrayBuffer())
          .then((buffer) => ({
            fileName,
            style,
            data: arrayBufferToBase64(buffer),
          })),
      ),
    ).then((fonts) => {
      calibriFontData = fonts;
      return fonts;
    });
  }

  const fonts = calibriFontData || (await calibriFontPromise);
  const registeredStyles = doc.getFontList().Calibri || [];

  fonts.forEach(({ fileName, style, data }) => {
    if (registeredStyles.includes(style)) return;
    doc.addFileToVFS(fileName, data);
    doc.addFont(fileName, "Calibri", style);
  });
};

// Vertical rhythm of a card, shared by the measure pass and the draw pass.
const cardMetrics = (isCompact) => ({
  padding: isCompact ? 2.8 : 4,
  rowStep: isCompact ? 3.3 : 5.5,
  shopStep: isCompact ? 2.9 : 4.4,
  shiftStep: isCompact ? 2.5 : 3.8,
  shopFont: isCompact ? 5.6 : 7.5,
  shiftFont: isCompact ? 5 : 6.8,
});

const staffShops = (staff) => (Array.isArray(staff.shops) ? staff.shops : []);

// Shop code line + one row per shift, for every shop
const shopBlockHeight = (staff, isCompact) => {
  const shops = staffShops(staff);
  if (!shops.length) return 0;
  const { shopStep, shiftStep } = cardMetrics(isCompact);
  return shops.reduce(
    (total, shop) =>
      total + shopStep + shiftStep * (shop.days?.length || 0) + 0.8,
    0,
  );
};

// Height a card needs so the shop table never runs past its border.
export const measurePayoutCardHeight = (staff, isCompact = true) => {
  const { padding, rowStep } = cardMetrics(isCompact);
  const optionalRows =
    (Number(staff.afterHrs || 0) > 0 ? 1 : 0) +
    (Number(staff.holidayHrs || 0) > 0 ? 1 : 0) +
    (Number(staff.fuelSupport || 0) ? 1 : 0) +
    (Number(staff.kiwiSaver ?? staff.kiwisaver ?? 0) ? 1 : 0);
  // name, pay period, payment date, divider, "Payout Summary" heading
  const headerHeight = isCompact ? 5 + 4.4 + 5.5 + 4.4 + 3.8 + 4.2 : 8 + 6 + 12 + 7 + 7 + 6;
  // divider, total, "Payment method", cash, bank, bottom padding
  const footerHeight = (isCompact ? 0.8 + 4.3 : 2 + 7) + rowStep * 4 + padding;
  return (
    headerHeight +
    rowStep * (2 + optionalRows) +
    shopBlockHeight(staff, isCompact) +
    footerHeight
  );
};

export const drawPayoutCard = (
  doc,
  staff,
  x,
  y,
  width,
  height,
  payPeriodText,
  payoutRates = {},
  compact,
) => {
  const isCompact = compact ?? height < 100;
  const { padding } = cardMetrics(isCompact);
  const left = x + padding;
  const right = x + width - padding;
  let currentY = y + (isCompact ? 5 : 8);
  const { rowStep, shopStep, shiftStep, shopFont, shiftFont } =
    cardMetrics(isCompact);

  const payrate = Number(staff.nonXeroPayrate ?? staff.payrate ?? 0);
  const regularHrs = Number(staff.regularHrs || 0);
  const afterHrs = Number(staff.afterHrs || 0);
  const holidayHrs = Number(staff.holidayHrs || 0);
  const bonus = Number(staff.bonus || 0);
  const afterHourRate = Number(payoutRates.afterHourRate ?? 1);
  const holidayRate = Number(payoutRates.holidayRate ?? 1);
  const category = String(staff.category || "").toUpperCase();
  const includeXeroHours = normalizeIncludeXeroHours(
    staff.includeXeroHours,
  );
  const xeroHours = Number(staff.xeroHours || 0);
  const fuelSupport = Number(staff.fuelSupport || 0);
  const kiwiSaver = Number(staff.kiwiSaver ?? staff.kiwisaver ?? 0);
  const totalPayout = Number(staff.totalPayout || 0);
  const cash = Number(staff.cash || 0);
  const bank = Number(staff.bank || 0);
  const isXeroIncludeHours = category === "XRP" && includeXeroHours;
  const isXeroExcludeHours = category === "XRP" && !includeXeroHours;
  const totalHrs = Number(
    staff.totalHrs ??
      regularHrs +
        afterHrs +
        holidayHrs * PAYOUT_HOLIDAY_HOURS_MULTIPLIER,
  );
  const baseHours = isXeroExcludeHours
    ? totalHrs
    : isXeroIncludeHours
      ? totalHrs - xeroHours
      : regularHrs;
  const basePay = baseHours * payrate;
  const afterAmount = afterHrs * afterHourRate * payrate;
  const holidayAmount = holidayHrs * holidayRate * payrate;
  const formatRate = (value) =>
    Number(value || 0).toLocaleString("en-NZ", {
      maximumFractionDigits: 2,
    });
  const regularLabel = isXeroIncludeHours
    ? `Regular (${totalHrs}hrs - ${xeroHours}hrs) x ${formatPdfMoney(payrate)}`
    : `Regular (${baseHours}hrs x ${formatPdfMoney(payrate)})`;

  const row = (label, value, bold = false) => {
    doc.setFont("Calibri", bold ? "bold" : "normal");
    doc.text(label, left, currentY);
    doc.text(value, right, currentY, { align: "right" });
    currentY += rowStep;
  };

  // Level 1: shop code + week total. Level 2: date / check in / check out / hours.
  const drawShopBreakdown = () => {
    const shops = staffShops(staff);
    if (!shops.length) return;

    const shopLeft = left + (isCompact ? 1.5 : 3);
    const dateX = shopLeft + (isCompact ? 2 : 4);
    const inX = dateX + (right - dateX) * 0.34;
    const outX = dateX + (right - dateX) * 0.62;
    const previousFontSize = doc.getFontSize();

    shops.forEach((shop) => {
      doc.setFont("Calibri", "bold");
      doc.setFontSize(shopFont);
      doc.setTextColor(60, 60, 60);
      doc.text(
        `${shop.code} (${Number(shop.hours || 0).toFixed(2)} hrs)`,
        shopLeft,
        currentY,
      );
      currentY += shopStep;

      doc.setFont("Calibri", "normal");
      doc.setFontSize(shiftFont);
      doc.setTextColor(80, 80, 80);
      (shop.days || []).forEach((day) => {
        doc.text(formatShiftDay(day.date), dateX, currentY);
        doc.text(formatShiftTime(day.checkInAt), inX, currentY);
        doc.text(formatShiftTime(day.checkOutAt), outX, currentY);
        doc.text(
          `${Number(day.hours || 0).toFixed(2)} hrs`,
          right,
          currentY,
          { align: "right" },
        );
        currentY += shiftStep;
      });
      currentY += 0.8;
    });

    doc.setTextColor(0, 0, 0);
    doc.setFontSize(previousFontSize);
  };

  doc.setDrawColor(130, 130, 130);
  doc.setLineWidth(0.2);
  doc.rect(x, y, width, height);

  doc.setFont("Calibri", "normal");
  doc.setFontSize(isCompact ? 8 : 11);
  doc.text(staff.fullName, left, currentY);
  currentY += isCompact ? 4.4 : 6;

  doc.setFont("Calibri", "italic");
  doc.setFontSize(isCompact ? 6.2 : 8.5);
  doc.text(`Pay period: ${payPeriodText}`, left, currentY);
  currentY += isCompact ? 5.5 : 12;

  doc.setFont("Calibri", "normal");
  doc.text(`Payment Date: ${formatPayoutDate(staff.paidTime)}`, left, currentY);
  doc.text(`Code: ${staff.category || ""}`, right, currentY, {
    align: "right",
  });
  currentY += isCompact ? 4.4 : 7;

  doc.setDrawColor(230, 230, 230);
  doc.line(left, currentY, right, currentY);
  currentY += isCompact ? 3.8 : 7;

  doc.setFont("Calibri", "bold");
  doc.setFontSize(isCompact ? 6.8 : 9.5);
  doc.text("Payout Summary", left, currentY);
  currentY += isCompact ? 4.2 : 6;

  doc.setFontSize(isCompact ? 6.2 : 8.5);
  row(
    isCompact ? regularLabel : `- ${regularLabel}`,
    formatPdfMoney(basePay),
  );
  drawShopBreakdown();
  doc.setFont("Calibri", "normal");
  doc.setFontSize(isCompact ? 6.2 : 8.5);
  if (afterHrs > 0) {
    row(
      isCompact
        ? `After (${afterHrs}hrs x ${formatRate(afterHourRate)})`
        : `- After-Hrs (${afterHrs}hrs x ${formatRate(afterHourRate)} x ${formatPdfMoney(payrate)})`,
      formatPdfMoney(afterAmount),
    );
  }
  if (holidayHrs > 0) {
    row(
      isCompact
        ? `Holiday (${holidayHrs}hrs x ${formatRate(holidayRate)})`
        : `- Holiday Hrs (${holidayHrs}hrs x ${formatRate(holidayRate)} x ${formatPdfMoney(payrate)})`,
      formatPdfMoney(holidayAmount),
    );
  }
  row(isCompact ? "Bonus" : "- Bonus", formatPdfMoney(bonus));
  if (fuelSupport) {
    row(isCompact ? "Fuel Support" : "- Fuel Support", formatPdfMoney(fuelSupport));
  }
  if (kiwiSaver) {
    row(isCompact ? "KiwiSaver" : "- KiwiSaver", formatPdfMoney(-kiwiSaver));
  }
  currentY += isCompact ? 0.8 : 2;

  doc.setDrawColor(230, 230, 230);
  doc.line(left, currentY, right, currentY);
  currentY += isCompact ? 4.3 : 7;

  doc.setFont("Calibri", "bold");
  doc.setFontSize(isCompact ? 7.2 : 10);
  row("TOTAL PAYOUT:", formatPdfMoney(totalPayout), true);
  doc.setFont("Calibri", "normal");
  doc.setFontSize(isCompact ? 6.2 : 8.5);
  doc.text("Payment method", left, currentY);
  currentY += rowStep;
  row(isCompact ? "Cash:" : "- Cash:", formatPdfMoney(cash));
  row(isCompact ? "Bank:" : "- Bank:", formatPdfMoney(bank));
};
